import React, { useEffect, useState } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { DenseTable } from '../shared/DenseTable';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RefreshCw, Download, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    start_date: '',
    end_date: ''
  });

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await fetch(`${API_URL}/api/audit-logs?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchActions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/audit-logs/actions`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setActions(data.actions || []);
      }
    } catch (error) {
      console.error('Failed to load actions', error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchActions();
  }, []);

  const handleFilter = () => {
    fetchLogs();
  };

  const handleClearFilters = () => {
    setFilters({ action: '', start_date: '', end_date: '' });
    fetchLogs();
  };

  const columns = [
    { 
      header: 'Time', 
      render: (row) => format(new Date(row.timestamp), 'dd/MM/yy HH:mm:ss')
    },
    { header: 'Action', accessor: 'action' },
    { header: 'User', accessor: 'user_name' },
    { 
      header: 'Details', 
      render: (row) => (
        <span className="text-xs text-muted-foreground max-w-xs truncate block">
          {JSON.stringify(row.details || {})}
        </span>
      )
    }
  ];

  return (
    <div className="space-y-4" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-xl font-bold">Audit Logs</h1>
        <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-logs">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <GroupBox label="Filters" className="p-3">
        <div className="grid grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Action Type</Label>
            <Select 
              value={filters.action || "all"} 
              onValueChange={(v) => setFilters({...filters, action: v === "all" ? "" : v})}
            >
              <SelectTrigger className="h-8" data-testid="filter-action">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actions.map(action => (
                  <SelectItem key={action} value={action}>{action}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Start Date</Label>
            <Input 
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({...filters, start_date: e.target.value})}
              className="h-8"
              data-testid="filter-start-date"
            />
          </div>
          <div>
            <Label className="text-xs">End Date</Label>
            <Input 
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({...filters, end_date: e.target.value})}
              className="h-8"
              data-testid="filter-end-date"
            />
          </div>
          <div className="flex items-end gap-2">
            <Button size="sm" onClick={handleFilter} data-testid="apply-filters">
              <Filter className="h-4 w-4 mr-1" />
              Apply
            </Button>
            <Button size="sm" variant="outline" onClick={handleClearFilters}>
              Clear
            </Button>
          </div>
        </div>
      </GroupBox>

      {/* Logs Table */}
      <GroupBox label={`Audit Logs (${logs.length})`}>
        <DenseTable 
          columns={columns} 
          data={logs}
          emptyMessage="No audit logs found"
        />
      </GroupBox>
    </div>
  );
}
